import React, { useState, useEffect, FC } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { Label, Input, Row, Col, Button, Alert } from "reactstrap";
import FormInput from "../../form-components/InputComponent";
import FormSelect from "../../form-components/SelectComponent";
import PhoneNumberInput from "../../form-components/PhoneNumberInput";
import Swal from "sweetalert2";
import { Icon } from "../../../components/Component";
import sites from "../../../api/master/sites";
import usersApis from "../../../api/master/users";
import deliveryAddressApis from "../../../api/master/deliveryAddress";
import { SequenceConfigModal, SequenceInput, useSequenceNumbering } from "../../../components/sequence";

interface SelectOptions {
  label: string;
  value: string;
}
const DeliveryAddressMaster: FC<{
  id?: string | null;
  formAction?: string | null;
  setAction?: () => void;
  toggleModelPopUp: () => void;
}> = ({ id, formAction, toggleModelPopUp }) => {
  const [countries, setCountries] = useState<SelectOptions[]>([]);
  const [states, setStates] = useState<SelectOptions[]>([]);
  const [districtOptions, setDistrictOptions] = useState<SelectOptions[]>([]);
  const [action, setAction] = useState(formAction);

  const methods = useForm({
    mode: "onChange",
  });

  const {
    handleSubmit,
    formState: { errors },
    setValue,
    trigger,
    watch,
    reset,
  } = methods;

  const sequence = useSequenceNumbering({
    moduleName: "Address",
    setValue,
    isEdit: action === "edit",
    id,
  });
  const [alertInfo, setAlertInfo] = useState({
    type: "", // 'success' or 'error'
    message: "",
    errors: [],
  });
  const [sitesOptions, setSitesOptions] = useState<SelectOptions[]>([]);
  const selectedCountry = watch("country");
  const selectedState = watch("state");

  useEffect(() => {
    if (selectedCountry) {
      fetchStatesByCountry(selectedCountry);
    }
  }, [selectedCountry]);

  const fetchCountrys = async (id?: string) => {
    try {
      const data = await usersApis.getCountrys();
      const countryOptions = data?.data?.map((country: any) => ({
        label: country.countryName,
        value: country.countryId.toString(),
      }));
      setCountries(countryOptions);
      fetchStatesByCountry(countryOptions[0].value);
      if (id) {
        const country = countryOptions.find(
          (country: SelectOptions) => country.value === id.toString()
        );
        if (!country) {
          return;
        }
        setValue("country", id);
      }
    } catch (error) { }
  };

  const getDistricts = async (stateID: any) => {
    try {
      const districts = await sites.getDistrictsByState(stateID);
      console.log(districts);
      if (districts.status) {
        if (districts.data.length > 0) {
          const transformedData = districts.data.map((item: any) => ({
            value: item.districtId.toString(),
            label: item.districtName,
          }));
          setDistrictOptions(transformedData);
        }
      }
    } catch (error: any) {
      console.log(error.message);
    }
  };

  const onStateChange = (e: any) => {
    console.log("e", e);
    getDistricts(e);
  };
  const fetchStatesByCountry = async (countryId: string, stateId?: string) => {
    try {
      const data = await usersApis.getStatesByCountry(countryId);
      const stateOptions = data?.data?.map((state: any) => ({
        value: state.stateId.toString(),
        label: state.stateName,
      }));
      if (stateOptions.length > 0) setStates(stateOptions);
      if (stateId) {
        const state = stateOptions.find(
          (state: SelectOptions) => state.value === stateId
        );
        if (!state) {
          return;
        }
        setValue("state", state.value);
      }
    } catch (error) { }
  };

  const onSubmit = async (data: any) => {
    if (!action && !id) {
      const payload: any = {
        ...sequence.buildPayload(data),
        country: 1,
      };

      const response = await deliveryAddressApis.createDeliveryAddress(payload);
      const { status, message } = response;
      if (status) {
        Swal.fire("Created!", "Address has been created", "success");
        toggleModelPopUp();
      } else {
        let errorMessage = "An error occurred";
        const fullErrorMessage = `${errorMessage}\n${message}`;
        setAlertInfo({
          type: "error",
          message: fullErrorMessage,
          errors: [],
        });
      }
    } else if (action === "edit") {
      const response = await deliveryAddressApis.updateDeliveryAddressById(id, {
        ...data,
        country: 1,
      });
      const { status, message } = response;
      if (status) {
        Swal.fire("Updated!", "Address has been updated", "success");
        toggleModelPopUp();
      } else {
        let errorMessage = "An error occurred";
        const fullErrorMessage = `${errorMessage}\n${message}`;
        setAlertInfo({
          type: "error",
          message: fullErrorMessage,
          errors: [],
        });
      }
    }
  };
  const fetchSites = async () => {
    try {
      const res = await sites.fetchSites();
      const { data, status, message } = res;
      if (status) {
        let sitesOptions = data.map((item: any) => {
          return { label: item.name, value: item.siteID.toString() };
        });
        setSitesOptions(sitesOptions);
      }
    } catch (error) {
      console.log(error);
    }
  };
  const fetchDeliveryAddressById = async () => {
    try {
      const { data, status, message } =
        await deliveryAddressApis.fetchDeliveryAddressById(id);
      if (status) {
        getDistricts(data.state);
        reset((previousValues) => {
          const newData = Object.keys(previousValues).reduce((acc, key) => {
            return {
              ...acc,
              [key]: data[key]?.toString(),
            };
          }, {});
          return newData;
        });
        setValue("state", data.state?.toString());
        setValue("district", data.district?.toString());
        if (data.addressType) {
          setValue("addressType", data.addressType.toString());
        }
        sequence.setEditMode();
      } else {
        toggleModelPopUp();
        Swal.fire("Failed!", message, "error");
      }
    } catch (err) {
      throw err;
    }
  };
  useEffect(() => {
    fetchSites();
    fetchCountrys();
    sequence.fetchSequenceAccess();
  }, []);

  useEffect(() => {
    if (id) {
      fetchDeliveryAddressById();
    }
    if (selectedState) {
      getDistricts(selectedState);
    }
  }, [id]);

  return (
    <>
      <h5 className="title">
        {action === "edit"
          ? "Edit Address"
          : action === "view"
            ? "View Address"
            : "Add Address"}
      </h5>
      {alertInfo.message && (
        <div className="mb-3 mt-1">
          <Alert
            color={alertInfo.type === "error" ? "danger" : "success"}
            className="alert-icon"
          >
            <strong>{alertInfo.message}</strong>
            <Icon
              name={
                alertInfo.type === "error" ? "alert-circle" : "check-circle"
              }
            />
            {alertInfo.errors.length > 0 ? (
              <ul>
                {alertInfo.errors.map((err: any, index: any) => (
                  <li key={index}>{err}</li>
                ))}
              </ul>
            ) : (
              ""
            )}
          </Alert>
        </div>
      )}
      <div style={{ marginTop: "15px" }}></div>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="row">
            <div className="col-md-4">
              <FormSelect
                name="addressType"
                label="Address Type"
                options={[
                  { label: "Billing", value: "Billing" },
                  { label: "Delivery", value: "Delivery" },
                ]}
                required={true}
                placeholder="Select Address Type"
              />
            </div>
        
            {!id && (
              <div className="col-md-4">
                <SequenceInput
                  label="Address ID"
                  numberingMode={sequence.numberingMode}
                  onSettingsClick={sequence.openModal}
                  showSettingsIcon={action !== "edit" && !id}
                  maxLength={30}
                />
              </div>
            )}

            <div className={!id ? "col-md-4" : "col-md-8"}>
              <FormSelect
                name="siteId"
                label="Site"
                options={sitesOptions}
                required={true}
                placeholder="Select Site"
              />
            </div>
          </div>
          <div style={{ marginTop: "15px" }}></div>
          <div className="row">
            <div className="col-md-4">
              <FormInput
                name="deliveryLocationName"
                label="Location Name"
                disabled={action === "view"}
                type="text"
                placeholder="Enter Location Name"
                required={true}
              />
            </div>
            <div className="col-md-4">
              <FormSelect
                name="state"
                label="State"
                options={states}
                required={true}
                placeholder="Select State"
                onChange={(ev) => onStateChange(ev)}
              />
            </div>
            <div className="col-md-4">
              <FormSelect
                name="district"
                label="District"
                options={districtOptions}
                required={true}
                placeholder="Select District"
              />
            </div>
          </div>
          <div style={{ marginTop: "15px" }}></div>
          <div className="row">
            <div className="col-md-4">
              <FormInput
                name="city"
                label="City/Town"
                placeholder="Enter City"
                required={true}
                type="text"
              />
            </div>
            <div className="col-md-4">
              <FormInput
                type="text"
                name="addressLine1"
                label="Address Line 1"
                required={true}
                placeholder="Enter Address"
              />
            </div>
            <div className="col-md-4">
              <FormInput
                type="text"
                name="addressLine2"
                label="Address Line 2"
                placeholder="Enter Address"
              />
            </div>
          </div>
          <div style={{ marginTop: "15px" }}></div>
          <div className="row">
            <div className="col-md-4">
              <FormInput
                type="number"
                name="pincode"
                label="Pincode/Zipcode"
                placeholder="Enter Pincode/Zipcode"
                required={true}
                pattern={/^\d+$/} // Allows only digits
                onInput={(e: any) => {
                  e.target.value = e.target.value.replace(/[^0-9]/g, ""); // Removes non-numeric characters
                }}
              />
            </div>
            <div className="col-md-4">
              <FormInput
                type="text"
                name="contactPersonName"
                label="Contact Person"
                required={true}
                placeholder="Enter Contact Person"
                pattern={/^[A-Za-z\s]+$/}
                onInput={(e: any) => {
                  e.target.value = e.target.value.replace(/[^A-Za-z\s]/g, ""); // Removes invalid characters
                }}
              />
            </div>
            <div className="col-md-4">
              <PhoneNumberInput
                name="mobileNumber"
                label="Contact Mobile"
                required={true}
                placeholder="Enter Contact Mobile"
              />
            </div>
          </div>
          <div style={{ marginTop: "15px" }}></div>
          <div className="row">
            <div className="col-md-4">
              <FormInput
                type="email"
                name="email"
                label="Contact Mail"
                placeholder="Enter Contact Mail"
                pattern={/^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,24}$/}
              />
            </div>
          </div>

          {action !== "view" ? (
            <Row className="mt-4">
              <Col lg="12">
                <ul className="align-center flex-wrap flex-sm-nowrap gx-4 gy-2">
                  <li>
                    <Button
                      className="btn btn-primary btn-md"
                      type="submit"
                      color="primary"
                    >
                      <span>{action === "edit" ? "Update" : "Submit"}</span>
                    </Button>
                  </li>
                  <li>
                    <a
                      href="#cancel"
                      onClick={(ev) => {
                        ev.preventDefault();
                        toggleModelPopUp();
                      }}
                      className="link link-light"
                    >
                      Cancel
                    </a>
                  </li>
                </ul>
              </Col>
            </Row>
          ) : (
            <Row className="mt-4">
              <Col lg="12">
                <ul className="align-center flex-wrap flex-sm-nowrap gx-4 gy-2">
                  <li>
                    <div
                      className="btn btn-primary btn-md"
                      color="primary"
                      onClick={() => setAction("edit")}
                    >
                      <Icon name="pen" />
                      <span>Edit</span>
                    </div>
                  </li>
                </ul>
              </Col>
            </Row>
          )}
        </form>
      </FormProvider>

      <SequenceConfigModal
        moduleName="Address"
        fieldLabel="Address ID"
        show={sequence.showNumberingModal}
        tempNumberingMode={sequence.tempNumberingMode}
        setTempNumberingMode={sequence.setTempNumberingMode}
        canAuto={sequence.canAuto}
        currentSequence={sequence.currentSequence}
        activeSequences={sequence.activeSequences}
        onSave={sequence.saveModalSelection}
        onClose={sequence.closeModal}
      />
    </>
  );
};

export default DeliveryAddressMaster;
